import { getOfflineDb, type QueueItem, type QueueItemStatus } from './db.js';

/**
 * Phase 10 — queueing primitives consumed by the sync engine and the
 * thin API wrappers in `apps/web/src/api/`. The queue is FIFO within
 * a `groupKey` (e.g. all answers for one inspection) and parallel
 * across groups.
 */

function randomId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export interface EnqueueArgs {
  userId: string;
  orgSlug: string;
  endpoint: string;
  method: QueueItem['method'];
  body: unknown;
  /** Local id for optimistic UI (e.g. `tmp_${uuid}`). */
  localId?: string;
  /** Photos referenced by this mutation — uploaded first. */
  photoIds?: string[];
  /** Items in the same group retry sequentially. */
  groupKey?: string;
  /**
   * Optional explicit idempotency key — usually derive automatically
   * so retries reuse the same key.
   */
  idempotencyKey?: string;
}

export async function enqueue(args: EnqueueArgs): Promise<QueueItem> {
  const now = Date.now();
  const item: QueueItem = {
    id: randomId(),
    userId: args.userId,
    orgSlug: args.orgSlug,
    endpoint: args.endpoint,
    method: args.method,
    body: args.body,
    idempotencyKey: args.idempotencyKey ?? randomId(),
    localId: args.localId ?? null,
    groupKey: args.groupKey ?? null,
    photoIds: args.photoIds ?? [],
    status: 'pending',
    attemptCount: 0,
    nextAttemptAt: now,
    lastError: null,
    conflict: null,
    createdAt: now,
    updatedAt: now,
  };
  await getOfflineDb().queueItems.put(item);
  return item;
}

export async function listPending(userId: string): Promise<QueueItem[]> {
  return getOfflineDb()
    .queueItems.where('[userId+status]')
    .equals([userId, 'pending' as QueueItemStatus])
    .sortBy('createdAt');
}

export async function listAll(userId: string): Promise<QueueItem[]> {
  return getOfflineDb()
    .queueItems.where('userId')
    .equals(userId)
    .sortBy('createdAt');
}

export async function countPending(userId: string): Promise<number> {
  const db = getOfflineDb();
  const pending = await db.queueItems
    .where('[userId+status]')
    .equals([userId, 'pending'])
    .count();
  const inFlight = await db.queueItems
    .where('[userId+status]')
    .equals([userId, 'in_flight'])
    .count();
  return pending + inFlight;
}

export async function countConflicts(userId: string): Promise<number> {
  return getOfflineDb()
    .queueItems.where('[userId+status]')
    .equals([userId, 'conflict'])
    .count();
}

export async function markInFlight(id: string): Promise<void> {
  await getOfflineDb().queueItems.update(id, {
    status: 'in_flight',
    updatedAt: Date.now(),
  });
}

export async function markSucceeded(id: string): Promise<void> {
  // Successful items can be deleted outright — we keep no audit
  // beyond the server side. (Failure rows persist for review.)
  await getOfflineDb().queueItems.delete(id);
}

export async function markFailed(
  id: string,
  error: string,
  delayMs: number,
): Promise<void> {
  const db = getOfflineDb();
  const row = await db.queueItems.get(id);
  if (!row) return;
  await db.queueItems.update(id, {
    status: 'pending',
    lastError: error,
    attemptCount: row.attemptCount + 1,
    nextAttemptAt: Date.now() + delayMs,
    updatedAt: Date.now(),
  });
}

export async function markPermanentlyFailed(id: string, error: string): Promise<void> {
  await getOfflineDb().queueItems.update(id, {
    status: 'failed',
    lastError: error,
    updatedAt: Date.now(),
  });
}

export async function markConflict(
  id: string,
  payload: NonNullable<QueueItem['conflict']>,
): Promise<void> {
  const db = getOfflineDb();
  const row = await db.queueItems.get(id);
  if (!row) return;
  await db.queueItems.update(id, {
    status: 'conflict',
    conflict: payload,
    updatedAt: Date.now(),
  });
  await db.conflicts.put({
    id: row.id,
    userId: row.userId,
    orgSlug: row.orgSlug,
    endpoint: row.endpoint,
    method: row.method,
    conflict: payload,
    body: row.body,
    createdAt: Date.now(),
  });
}

export async function discardConflict(id: string): Promise<void> {
  const db = getOfflineDb();
  await db.transaction('rw', db.queueItems, db.conflicts, async () => {
    await db.queueItems.delete(id);
    await db.conflicts.delete(id);
  });
}

export async function retryConflict(id: string): Promise<void> {
  const db = getOfflineDb();
  await db.transaction('rw', db.queueItems, db.conflicts, async () => {
    const row = await db.queueItems.get(id);
    if (!row) return;
    await db.queueItems.update(id, {
      status: 'pending',
      conflict: null,
      attemptCount: 0,
      nextAttemptAt: Date.now(),
      lastError: null,
      updatedAt: Date.now(),
    });
    await db.conflicts.delete(id);
  });
}

/**
 * Exponential backoff with light jitter, capped at 60 s. Phase 10
 * spec asks for 1 s, 4 s, 16 s, 60 s and then plateau.
 */
export function computeBackoff(attempt: number): number {
  const base = [1000, 4000, 16000, 60000];
  const idx = Math.min(attempt, base.length - 1);
  const baseDelay = base[idx]!;
  const jitter = Math.floor(Math.random() * 250);
  return baseDelay + jitter;
}
